/** 계정 설정 API. 기본 배경(`W-80`) 하나.
 *
 * ⭐ **사용자 결정 2026-10-28 (노션 「결정 대기」 (다)안, 법무 `L-C38`).** 지금 서버에 쓰는 계정 설정은 기본
 * 배경 하나다. 사진 이용 스위치(`W-70`)는 #245 이전처럼 **저장하지 않는다**(`screens/Account.tsx`).
 *
 * **설정은 계정 단위다**(목업 `W-80`: 앱에서 한 번 정하면 웹에도 적용). 그래서 브라우저에 따로
 * 담지 않는다. 브라우저에 담으면 앱과 웹이 같은 값을 두 자리에서 들게 되고, 목업이 그 상태를
 * 「어느 쪽이 이겼는지 셀러가 알 수 없다」며 막았다.
 *
 * ## 서버가 쓰는지는 서버가 말한다 (`savesToAccount`)
 *
 * 운영은 처리방침 개정 시행일까지 계정 설정을 서버에 쓰지 않는다(`ACCOUNT_SETTINGS_WRITE`). 그동안 `W-80` 은
 * #245 이전처럼 고른 색을 화면 상태로만 든다(새로고침하면 처음 값). `PATCH` 가 `409 ACCOUNT_SETTINGS_WRITE_OFF`
 * 로 거절하면(스위치가 바뀐 사이) 같은 갈래로 간다(`isAccountSettingsWriteOff`).
 *
 * ## 경로는 아직 이 파일에 있다
 *
 * 정본 자리는 `@makit/shared` 의 `authEndpoints` 인데 그 파일은 이 작업의 자리가 아니다. 응답 모양은
 * shared 타입(`SellerSettingsView`)에서 뽑고, 서버(`routes/auth.ts` `AccountSettingsView`)와 앱도 같은 모양이다.
 *
 * ## 요청은 `lib/api.ts` 의 `request` 로 보낸다
 *
 * 주소 · 쿠키(`credentials: "include"`) · 실패(`ApiError(status, code)`)가 다른 요청과 같다. **401 이면 세션 게이트를 깨운다.**
 * 409 의 `code`(`ACCOUNT_SETTINGS_WRITE_OFF`)도 `request` 가 읽어 `ApiError.code` 에 싣는다
 * (`isAccountSettingsWriteOff`). 2026-10-28 코드리뷰 전에는 같은 일을 이 파일이 따로 했다.
 */

import type { SellerSettingsPatch, SellerSettingsView } from "@makit/shared";

import { ApiError, request } from "./api";

const ACCOUNT_SETTINGS_PATH = "/api/auth/account/settings";

/** `GET /api/auth/account/settings`. 서버 `AccountSettingsView` 와 같은 모양이다. */
export type AccountSettings = Pick<SellerSettingsView, "defaultBackgroundColor"> & {
  /** 이 배포가 계정 설정을 서버에 쓰는가. `false` 면 기본 배경은 화면 상태로만 든다. */
  savesToAccount: boolean;
};

/** 기본 배경 저장 요청. shared `SellerSettingsPatch` 에서 그 칸 하나만 쓴다. */
type BackgroundPatch = Required<Pick<SellerSettingsPatch, "defaultBackgroundColor">>;

/** 서버가 쓰기 스위치가 꺼져 있어 거절할 때의 코드(`routes/auth.ts` `ACCOUNT_SETTINGS_WRITE_OFF`). */
const WRITE_OFF_CODE = "ACCOUNT_SETTINGS_WRITE_OFF";

/** react-query 열쇠. 웹은 로그아웃 때 클라이언트를 통째로 바꾸지만(`app/SessionGate.tsx`) 앱과 같은 열쇠를 쓴다.
 *
 * ⛔ **`"auth"` 아래에 두지 않는다**(2026-10-25 재리뷰). 로그아웃 청소가 `"auth"` 로 시작하는 키만 남기고
 * 나머지를 버린다. 이 값은 셀러의 데이터라 남으면 **다음에 로그인한 셀러가 앞 셀러의 기본 배경을 본다.**
 * `"account"` 아래에 두면 그 청소가 함께 버린다. */
export const ACCOUNT_SETTINGS_QUERY_KEY = ["account", "settings"] as const;

export function fetchAccountSettings(): Promise<AccountSettings> {
  return request<AccountSettings>(ACCOUNT_SETTINGS_PATH);
}

/** 기본 배경. 서버는 대문자 hex 로 맞춰 돌려준다. **이 칸 하나만 보낸다.** */
export function saveDefaultBackground(hex: string): Promise<BackgroundPatch> {
  const body: BackgroundPatch = { defaultBackgroundColor: hex };
  return request<BackgroundPatch>(ACCOUNT_SETTINGS_PATH, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/** 서버가 쓰기 스위치가 꺼져 있어 거절했는가. 그때는 실패가 아니라 「화면 상태로만 든다」 갈래다. */
export function isAccountSettingsWriteOff(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409 && error.code === WRITE_OFF_CODE;
}
